"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import type { FolderWithChildren } from "@/types/chat";
import type { Chatbot } from "@/types/chatbot";
type NewThreadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateThread: (name: string, folderId: string | null, chatbotId?: string | null) => void;
  folderTree: FolderWithChildren[];
  defaultFolderId?: string | null;
  /** List of available chatbots to choose from */
  chatbots?: Chatbot[];
  /** Pre-selected chatbot ID (when starting from a chatbot's + button) */
  chatbotId?: string | null;
  /** Display name of the pre-selected chatbot */
  chatbotName?: string | null;
};
type FolderItemProps = {
  folder: FolderWithChildren;
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
  depth?: number;
};
function FolderItem(t0) {
  const $ = _c(33);
  const {
    folder,
    selectedFolderId,
    onSelect,
    depth: t1
  } = t0;
  const depth = t1 === undefined ? 0 : t1;
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder.id;
  let t2;
  if ($[0] !== folder.id || $[1] !== onSelect) {
    t2 = () => onSelect(folder.id);
    $[0] = folder.id;
    $[1] = onSelect;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `w-full flex items-center gap-2 px-3 py-2 text-left rounded-md transition-colors cursor-pointer ${isSelected ? "bg-blue-600/30 text-blue-200 ring-1 ring-blue-500/50" : "hover:bg-slate-700/50 text-slate-300"}`;
  const t4 = `${depth * 16 + 12}px`;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      paddingLeft: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== hasChildren || $[6] !== isExpanded) {
    t6 = hasChildren && <button type="button" onClick={e => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }} className="p-0.5 hover:bg-slate-600 rounded"><svg className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>;
    $[5] = hasChildren;
    $[6] = isExpanded;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = `w-4 h-4 flex-shrink-0 ${isSelected ? "text-blue-400" : "text-yellow-500"}`;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t7) {
    t9 = <svg className={t7} fill="currentColor" viewBox="0 0 20 20">{t8}</svg>;
    $[9] = t7;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== folder.name) {
    t10 = <span className="truncate text-sm">{folder.name}</span>;
    $[11] = folder.name;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== folder.is_default) {
    t11 = folder.is_default && <span className="text-xs text-slate-500 ml-1">(default)</span>;
    $[13] = folder.is_default;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t2 || $[18] !== t3 || $[19] !== t5 || $[20] !== t6 || $[21] !== t9) {
    t12 = <div onClick={t2} className={t3} style={t5}>{t6}{t9}{t10}{t11}</div>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t2;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== depth || $[24] !== folder.children || $[25] !== hasChildren || $[26] !== isExpanded || $[27] !== onSelect || $[28] !== selectedFolderId) {
    t13 = hasChildren && isExpanded && <div>{folder.children.map(child => <FolderItem key={child.id} folder={child} selectedFolderId={selectedFolderId} onSelect={onSelect} depth={depth + 1} />)}</div>;
    $[23] = depth;
    $[24] = folder.children;
    $[25] = hasChildren;
    $[26] = isExpanded;
    $[27] = onSelect;
    $[28] = selectedFolderId;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
export default function NewThreadModal(t0) {
  const $ = _c(72);
  const {
    isOpen,
    onClose,
    onCreateThread,
    folderTree,
    defaultFolderId,
    chatbots: t1,
    chatbotId
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const chatbots = t2;
  const [threadName, setThreadName] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState(defaultFolderId || null);
  const [selectedChatbotId, setSelectedChatbotId] = useState(chatbotId || null);
  const inputRef = useRef(null);
  let t3;
  let t4;
  if ($[2] !== chatbotId || $[3] !== defaultFolderId || $[4] !== isOpen) {
    t3 = () => {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
        setThreadName("");
        setSelectedFolderId(defaultFolderId || null);
        setSelectedChatbotId(chatbotId || null);
      }
    };
    t4 = [isOpen, defaultFolderId, chatbotId];
    $[2] = chatbotId;
    $[3] = defaultFolderId;
    $[4] = isOpen;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isOpen || $[8] !== onClose) {
    t5 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }
    };
    t6 = [isOpen, onClose];
    $[7] = isOpen;
    $[8] = onClose;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== onClose || $[12] !== onCreateThread || $[13] !== selectedChatbotId || $[14] !== selectedFolderId || $[15] !== threadName) {
    t7 = e_0 => {
      e_0.preventDefault();
      const trimmedName = threadName.trim();
      if (trimmedName) {
        onCreateThread(trimmedName, selectedFolderId, selectedChatbotId);
        onClose();
      }
    };
    $[11] = onClose;
    $[12] = onCreateThread;
    $[13] = selectedChatbotId;
    $[14] = selectedFolderId;
    $[15] = threadName;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleSubmit = t7;
  let t8;
  if ($[17] !== chatbots || $[18] !== selectedChatbotId) {
    let t9;
    if ($[20] !== selectedChatbotId) {
      t9 = c => c.id === selectedChatbotId;
      $[20] = selectedChatbotId;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = chatbots.find(t9);
    $[17] = chatbots;
    $[18] = selectedChatbotId;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const selectedChatbot = t8;
  let t9;
  if ($[22] !== handleSubmit) {
    t9 = e_1 => {
      if (e_1.key === "Enter" && !e_1.shiftKey) {
        e_1.preventDefault();
        handleSubmit(e_1);
      }
    };
    $[22] = handleSubmit;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleKeyDown_0 = t9;
  const findDefaultFolder = folders => {
    for (const folder of folders) {
      if (folder.is_default) {
        return folder.id;
      }
      if (folder.children) {
        const found = findDefaultFolder(folder.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };
  defaultFolderId || findDefaultFolder(folderTree);
  if (!isOpen) {
    return null;
  }
  let t10;
  if ($[24] !== onClose) {
    t10 = <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />;
    $[24] = onClose;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2 className="text-lg font-medium text-slate-100">New Thread</h2>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== onClose) {
    t13 = <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">{t11}<button type="button" onClick={onClose} className="p-1 hover:bg-slate-700 rounded transition-colors">{t12}</button></div>;
    $[28] = onClose;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <label className="block text-sm font-medium text-slate-400 mb-2">Save to Directory</label>;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== folderTree || $[32] !== selectedFolderId) {
    t15 = <div className="bg-slate-800 border border-slate-600 rounded-md max-h-48 overflow-y-auto">{folderTree.length === 0 ? <div className="px-3 py-4 text-sm text-slate-500 text-center">No directories found</div> : <div className="py-1">{folderTree.map(folder_0 => <FolderItem key={folder_0.id} folder={folder_0} selectedFolderId={selectedFolderId} onSelect={setSelectedFolderId} />)}</div>}</div>;
    $[31] = folderTree;
    $[32] = selectedFolderId;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== folderTree.length || $[35] !== selectedFolderId) {
    t16 = selectedFolderId === null && folderTree.length > 0 && <p className="text-xs text-amber-400 mt-1">Please select a directory to save the thread</p>;
    $[34] = folderTree.length;
    $[35] = selectedFolderId;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <div className="px-4 py-3 border-b border-slate-700">{t14}{t15}{t16}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== chatbots || $[41] !== selectedChatbot || $[42] !== selectedChatbotId) {
    t18 = chatbots.length > 0 && <div className="px-4 py-3 border-b border-slate-700"><label className="block text-sm font-medium text-slate-400 mb-2">Chatbot Configuration</label><select value={selectedChatbotId || ""} onChange={e_2 => setSelectedChatbotId(e_2.target.value || null)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"><option value="">Default (no chatbot)</option>{chatbots.map(_temp)}</select>{selectedChatbot && <p className="text-xs text-slate-500 mt-1">Thread will use settings from {selectedChatbot.name}.chatbot</p>}</div>;
    $[40] = chatbots;
    $[41] = selectedChatbot;
    $[42] = selectedChatbotId;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <label className="block text-sm font-medium text-slate-400 mb-2">Thread Name</label>;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_3 => setThreadName(e_3.target.value);
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== handleKeyDown_0 || $[47] !== threadName) {
    t21 = <input ref={inputRef} type="text" value={threadName} onChange={t20} onKeyDown={handleKeyDown_0} placeholder="Enter thread name..." className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />;
    $[46] = handleKeyDown_0;
    $[47] = threadName;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== onClose) {
    t22 = <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-slate-100 hover:bg-slate-700 rounded-md transition-colors">Cancel</button>;
    $[49] = onClose;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== folderTree.length || $[52] !== selectedFolderId || $[53] !== threadName) {
    t23 = !threadName.trim() || selectedFolderId === null && folderTree.length > 0;
    $[51] = folderTree.length;
    $[52] = selectedFolderId;
    $[53] = threadName;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t23) {
    t24 = <button type="submit" disabled={t23} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Create Thread</button>;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t22 || $[58] !== t24) {
    t25 = <div className="flex justify-end gap-3 mt-4">{t22}{t24}</div>;
    $[57] = t22;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== handleSubmit || $[61] !== t21 || $[62] !== t25) {
    t26 = <form onSubmit={handleSubmit} className="px-4 py-4">{t19}{t21}{t25}</form>;
    $[60] = handleSubmit;
    $[61] = t21;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t13 || $[65] !== t17 || $[66] !== t18 || $[67] !== t26) {
    t27 = <div className="relative bg-slate-900 border border-slate-700 rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden">{t13}{t17}{t18}{t26}</div>;
    $[64] = t13;
    $[65] = t17;
    $[66] = t18;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t10 || $[70] !== t27) {
    t28 = createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center">{t10}{t27}</div>, document.body);
    $[69] = t10;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  return t28;
}
function _temp(chatbot) {
  return <option key={chatbot.id} value={chatbot.id}>{chatbot.name}.chatbot</option>;
}